import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Paper, Typography, TextField, IconButton, Divider,
  Table, TableHead, TableRow, TableCell, TableBody, Alert, Container,
  Tabs, Tab, Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../reduxStore/Store';
import { Article, GeneriqueData, Ligne_Document_Vente, Reglement, Tiers } from '../models/models';
import { PERMISSION_PRIX, PERMISSION_REMISE, TypeLigne } from '../constants/enums';
import { usePermission } from '../hooks/usePermission';
import { fetchArticleByFilters } from '../reduxStore/ArticleSlice';
import { fetchSysTypeDocuments } from '../reduxStore/SysTypeDocumentSlice';
import { createPosSale, clearMessages } from '../reduxStore/PosSlice';
import { useSnackbar } from '../components/SnackbarProvider';
import SelectCaisse from '../components/Selects/SelectCaisse';
import SelectDepot from '../components/Selects/SelectDepot';
import SelectModeReglement from '../components/Selects/SelectModeReglement';
import ClientAutoC from '../components/AutoComplete/ClientAutoC';
import ArticleAutoC from '../components/AutoComplete/ArticleAutoC';
import MyButton from '../components/MyButton';
import { ButtonType } from '../constants/enums';
import PosTicket from '../components/Prints/PosTicket';
import { formatNumberWithSpaces } from '../utils/utils';

interface CartLine {
  article: Article;
  quantite: number;
  prix_unitaire: number;
  remise: number;
  tva: number;
}

interface Ticket {
  id:              string;          // uid local
  label:           string;          // "Ticket 1", etc.
  cart:            CartLine[];
  client:          Tiers | null;
  montantEncaisse: number;
}

const STORAGE_KEY = 'pos_tabs_v1';
const newId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
/** AAAA-MM-JJ — même calcul que les autres formulaires (DocumentVenteForm, ReglementForm). */
const dateDuJour = () => new Date().toISOString().split('T')[0];
const emptyTicket = (label: string): Ticket => ({
  id: newId(), label, cart: [], client: null, montantEncaisse: 0,
});

// prix_unitaire est HT (article.prix_v est HT).
// On reproduit la colonne générée DB : total_ttc = quantite * prix_unitaire * (1 - remise/100) * (1 + tva/100).
const computeLineTotal = (l: CartLine) =>
  l.quantite * l.prix_unitaire * (1 - (l.remise ?? 0) / 100) * (1 + (l.tva ?? 0) / 100);

// ── Persistance localStorage ───────────────────────────────────────────────
const loadTickets = (): { tickets: Ticket[]; activeId: string } => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { tickets: Ticket[]; activeId: string };
      if (parsed.tickets?.length > 0) {
        // Recale activeId si désynchronisé (ancien localStorage corrompu)
        const exists = parsed.tickets.some(t => t.id === parsed.activeId);
        return exists ? parsed : { tickets: parsed.tickets, activeId: parsed.tickets[0].id };
      }
    }
  } catch { /* ignore — données corrompues, on repart à vide */ }
  const t = emptyTicket('Ticket 1');
  return { tickets: [t], activeId: t.id };
};

const PointOfSale: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useSnackbar();
  const { sysTypeData } = useSelector((state: RootState) => state.SysTypeDocumentSlice);
  const { selectedDepartement } = useSelector((state: RootState) => state.AppSlice);
  const { loading, error, successMessage, lastSale } = useSelector((state: RootState) => state.PosSlice);

  const factClientType = useMemo(
    () => sysTypeData.find((t) => t.code === 'FACT_CLIENT'),
    [sysTypeData]
  );

  // ── État TICKETS (per-tab) ───────────────────────────────────────────────
  // IMPORTANT : un seul appel à loadTickets() — sinon tickets[0].id ≠ activeId
  // car chaque appel génère un nouveau newId() en l'absence de localStorage.
  const initialPosRef = useRef<ReturnType<typeof loadTickets> | null>(null);
  if (initialPosRef.current === null) initialPosRef.current = loadTickets();
  const [tickets,  setTickets]  = useState<Ticket[]>(initialPosRef.current.tickets);
  const [activeId, setActiveId] = useState<string>(initialPosRef.current.activeId);

  // ── État partagé (en-tête caisse — commun à tous les tickets) ───────────
  const [depot, setDepot]                 = useState<GeneriqueData | null>(null);
  const [caisse, setCaisse]               = useState<GeneriqueData | null>(null);
  const [modeReglement, setModeReglement] = useState<GeneriqueData | null>(null);
  // Date de la vente (pièce, échéance et règlement) : pré-remplie au jour, modifiable,
  // commune à tous les tickets. Non persistée : rouvrir la caisse revient au jour.
  const [dateVente, setDateVente]         = useState<string>(dateDuJour);
  const [scannerValue, setScannerValue]   = useState('');
  const scannerRef                        = useRef<HTMLInputElement | null>(null);
  // Saisie rapide : Article (focus par défaut) → Quantité de la ligne → retour Article
  const articleRef                        = useRef<HTMLInputElement | null>(null);
  const qtyRefs                           = useRef<Record<number, HTMLInputElement | null>>({});
  const [articleResetKey, setArticleResetKey] = useState(0);
  const [focusQtyId, setFocusQtyId]       = useState<number | null>(null);
  const focusArticle = () => setTimeout(() => articleRef.current?.focus(), 0);
  // Sans le droit docVentes.remise : colonne remise masquée (garde backend LigneGuards.droitRemise)
  const peutRemise = usePermission(PERMISSION_REMISE);
  // Sans le droit docVentes.prix : prix catalogue en lecture seule (garde backend LigneGuards.droitPrix)
  const peutModifierPrix = usePermission(PERMISSION_PRIX);

  // Ticket actif (toujours défini grâce à loadTickets qui en garantit au moins 1)
  const activeTicket = tickets.find(t => t.id === activeId) ?? tickets[0];
  const cart            = activeTicket?.cart ?? [];
  const client          = activeTicket?.client ?? null;
  const montantEncaisse = activeTicket?.montantEncaisse ?? 0;

  const totalTTC = useMemo(
    () => cart.reduce((acc, l) => acc + computeLineTotal(l), 0),
    [cart]
  );

  // Helpers d'update ciblés sur le ticket actif
  const updateActive = (patch: Partial<Ticket>) => {
    setTickets(prev => prev.map(t => t.id === activeId ? { ...t, ...patch } : t));
  };
  const updateActiveCart = (fn: (prev: CartLine[]) => CartLine[]) => {
    setTickets(prev => prev.map(t => t.id === activeId ? { ...t, cart: fn(t.cart) } : t));
  };

  // ── Persistance ──────────────────────────────────────────────────────────
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ tickets, activeId }));
    } catch { /* quota dépassé — on ignore */ }
  }, [tickets, activeId]);

  // ── Effets ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (sysTypeData.length === 0) dispatch(fetchSysTypeDocuments());
    focusArticle();
    return () => { dispatch(clearMessages()); };
  }, [dispatch, sysTypeData.length]);

  // Auto-fill montant encaissé = total TTC du ticket actif
  useEffect(() => {
    updateActive({ montantEncaisse: Number(totalTTC.toFixed(2)) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalTTC, activeId]);

  // Reset après une vente réussie : ferme le ticket validé + impression
  useEffect(() => {
    if (successMessage && lastSale) {
      showSnackbar(successMessage, 'success');
      // Avertissements non bloquants des lignes (ex : vendu sous le prix de revient, stock)
      lastSale.lignes?.flatMap((l) => l.warnings ?? []).forEach((w) => showSnackbar(w, 'warning'));
      setTimeout(() => window.print(), 100);
      // Retire le ticket actif ; s'il était le dernier, on en recrée un vide
      setTickets(prev => {
        const remaining = prev.filter(t => t.id !== activeId);
        if (remaining.length === 0) {
          const t = emptyTicket('Ticket 1');
          setActiveId(t.id);
          return [t];
        }
        setActiveId(remaining[0].id);
        return remaining;
      });
      setScannerValue('');
      dispatch(clearMessages());
      focusArticle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successMessage, lastSale]);

  useEffect(() => {
    if (error) {
      showSnackbar(error, 'error');
      dispatch(clearMessages());
    }
  }, [error, dispatch, showSnackbar]);

  // ── Gestion des onglets ──────────────────────────────────────────────────
  const newTab = () => {
    const t = emptyTicket(`Ticket ${tickets.length + 1}`);
    setTickets(prev => [...prev, t]);
    setActiveId(t.id);
    focusArticle();
  };

  const closeTab = (id: string) => {
    const target = tickets.find(t => t.id === id);
    if (!target) return;
    if (target.cart.length > 0 && !window.confirm(`Fermer "${target.label}" ? Le contenu sera perdu.`)) return;
    setTickets(prev => {
      const remaining = prev.filter(t => t.id !== id);
      if (remaining.length === 0) {
        const t = emptyTicket('Ticket 1');
        setActiveId(t.id);
        return [t];
      }
      if (id === activeId) setActiveId(remaining[0].id);
      return remaining;
    });
  };

  // ── Ajout au panier ──────────────────────────────────────────────────────
  const addArticleToCart = (article: Article) => {
    if (!article?.id) return;
    updateActiveCart(prev => {
      const existing = prev.find((l) => l.article.id === article.id);
      if (existing) {
        return prev.map((l) =>
          l.article.id === article.id ? { ...l, quantite: l.quantite + 1 } : l
        );
      }
      return [...prev, {
        article,
        quantite:      1,
        prix_unitaire: article.prix_v ?? 0,
        remise:        0,
        tva:           article.tva ?? 0,
      }];
    });
  };

  // Choix dans le champ Article : ajout + focus sur la quantité de la ligne
  const handleArticleSelected = (article: Article | null) => {
    if (!article?.id) return;
    addArticleToCart(article);
    setArticleResetKey(k => k + 1); // vide le champ, même si l'article était déjà au panier
    setFocusQtyId(article.id);
  };

  // La ligne n'existe qu'après le rendu suivant l'ajout → focus différé
  useEffect(() => {
    if (focusQtyId == null) return;
    const el = qtyRefs.current[focusQtyId];
    if (el) {
      el.focus();
      el.select();
    }
    setFocusQtyId(null);
  }, [focusQtyId, cart]);

  // Quantité : Entrée ou Tab → retour au champ Article
  const handleQtyKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey)) {
      e.preventDefault();
      focusArticle();
    }
  };

  // Scanner : Enter → recherche par code exact → ajout panier actif
  const handleScannerKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const code = scannerValue.trim();
    if (!code) return;
    try {
      const result = await dispatch(fetchArticleByFilters({ code, visible: 1, limit: 1, offset: 0 })).unwrap();
      const found = result.data?.[0];
      if (!found) {
        showSnackbar(`Article "${code}" introuvable.`, 'warning');
      } else {
        addArticleToCart(found);
      }
    } catch (err: any) {
      showSnackbar('Erreur recherche article.', 'error');
    } finally {
      setScannerValue('');
      scannerRef.current?.focus();
    }
  };

  const updateQuantite = (articleId: number, quantite: number) => {
    updateActiveCart(prev => prev
      .map((l) => l.article.id === articleId ? { ...l, quantite: Math.max(0, quantite) } : l)
      .filter((l) => l.quantite > 0));
  };

  const removeLine = (articleId: number) => {
    updateActiveCart(prev => prev.filter((l) => l.article.id !== articleId));
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const handleValider = async () => {
    if (!factClientType) {
      showSnackbar('Type FACT_CLIENT introuvable.', 'error'); return;
    }
    if (cart.length === 0)              { showSnackbar('Panier vide.', 'warning'); return; }
    if (!caisse?.id)                    { showSnackbar('Caisse obligatoire.', 'error'); return; }
    if (!depot?.id)                     { showSnackbar('Dépôt obligatoire.', 'error'); return; }
    if (!modeReglement?.id)             { showSnackbar('Mode de règlement obligatoire.', 'error'); return; }
    if (!dateVente)                     { showSnackbar('Date obligatoire.', 'error'); return; }
    if (Math.round(montantEncaisse * 100) < Math.round(totalTTC * 100)) {
      showSnackbar('Montant encaissé insuffisant.', 'error'); return;
    }
    if (factClientType.tiers_obligatoire && !client?.id) {
      showSnackbar('Client obligatoire.', 'error'); return;
    }

    const payload = {
      document: {
        id:             -1,
        contrat_id:     null,
        numero_doc:     '',
        date_doc:       dateVente,
        date_echeance:  dateVente,
        tiers_id:       client?.id ?? 0,
        reference:      null,
        type_doc_id:    factClientType.id,
        doc_code:       factClientType.code,
        depot_id:       depot.id,
        departement_id: selectedDepartement?.id ?? null,
        tiers:          null,
        contrat:        null,
        depot:          null,
      },
      lignes: cart.map<Ligne_Document_Vente>((l) => ({
        id:                -1,
        document_vente_id: -1,
        article_id:        l.article.id!,
        quantite:          l.quantite,
        prix_unitaire:     l.prix_unitaire,
        remise:            l.remise,
        tva:               l.tva,
        depot_id:          depot.id,
        type_ligne:        TypeLigne.ARTICLE,
      })),
      reglement: {
        id:               -1,
        date_reglement:   dateVente,
        numero_reglement: '',
        montant:          montantEncaisse,
        sens:             'C' as const,
        type_reglement:   'ENCAISSEMENT' as const,
        caisse_id:        caisse.id,
        mode_paiement_id: modeReglement.id,
        tiers_id:         client?.id ?? null,
        departement_id:   selectedDepartement?.id ?? null,
      } as Reglement,
    };

    dispatch(createPosSale(payload));
  };

  // ── Rendu ─────────────────────────────────────────────────────────────────
  if (!selectedDepartement) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="info">Sélectionnez un département pour démarrer la caisse.</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1, height: 'calc(100vh - 100px)' }}>
      {/* Barre d'onglets */}
      <Paper variant="outlined" sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
        <Tabs
          value={activeId}
          onChange={(_, v) => setActiveId(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ flex: 1, minHeight: 40 }}
        >
          {tickets.map(t => {
            const total = t.cart.reduce((s, l) => s + computeLineTotal(l), 0);
            return (
              <Tab
                key={t.id}
                value={t.id}
                sx={{ minHeight: 40, textTransform: 'none', pr: 1 }}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span>{t.label}</span>
                    {t.cart.length > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        ({t.cart.length} · {formatNumberWithSpaces(total)})
                      </Typography>
                    )}
                    <IconButton
                      size="small"
                      component="span"
                      onClick={(e) => { e.stopPropagation(); closeTab(t.id); }}
                      sx={{ ml: 0.5, p: 0.25 }}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  </Box>
                }
              />
            );
          })}
        </Tabs>
        <Tooltip title="Nouveau ticket">
          <IconButton size="small" onClick={newTab} color="primary">
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Paper>

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 380px' },
        gap: 2,
        flex: 1,
        overflow: 'auto',
        minHeight: 0,
      }}>
        {/* Colonne gauche : scanner + panier */}
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1, overflow: 'hidden' }}>
          <Typography variant="h6">Point de vente — {activeTicket?.label}</Typography>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Box sx={{ flex: 1 }}>
              <ArticleAutoC
                key={`${activeId}-${articleResetKey}`} // reset après ajout, scope par ticket
                externalRef={articleRef}
                onArticleChange={handleArticleSelected}
                selectedArticle={null}
              />
            </Box>
            <TextField
              inputRef={scannerRef}
              label="Scanner"
              placeholder="Code + Entrée"
              value={scannerValue}
              onChange={(e) => setScannerValue(e.target.value)}
              onKeyDown={handleScannerKeyDown}
              size="small"
              sx={{ width: 170, flexShrink: 0 }}
            />
          </Box>

          <Divider />

          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Désignation</TableCell>
                  <TableCell align="right" sx={{ width: 80 }}>Qté</TableCell>
                  <TableCell align="right" sx={{ width: 100 }}>Prix U. HT</TableCell>
                  <TableCell align="right" sx={{ width: 100 }}>Prix U. TTC</TableCell>
                  {peutRemise && <TableCell align="right" sx={{ width: 80 }}>Remise %</TableCell>}
                  <TableCell align="right" sx={{ width: 110 }}>Total TTC</TableCell>
                  <TableCell sx={{ width: 40 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {cart.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={peutRemise ? 8 : 7} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                      Panier vide — scannez ou recherchez un article.
                    </TableCell>
                  </TableRow>
                )}
                {cart.map((l) => (
                  <TableRow key={l.article.id}>
                    <TableCell>{l.article.code}</TableCell>
                    <TableCell>{l.article.designation}</TableCell>
                    <TableCell align="right">
                      <TextField
                        type="number"
                        value={l.quantite}
                        inputRef={(el) => { qtyRefs.current[l.article.id!] = el; }}
                        onChange={(e) => updateQuantite(l.article.id!, Number(e.target.value))}
                        onKeyDown={handleQtyKeyDown}
                        onFocus={(e) => setTimeout(() => e.target.select(), 0)}
                        size="small"
                        inputProps={{ min: 0, step: 1, inputMode: 'numeric', style: { textAlign: 'right', width: 60, fontSize: 18 } }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        type="number"
                        value={l.prix_unitaire}
                        onChange={(e) => {
                          const val = Number(e.target.value);
                          updateActiveCart(prev => prev.map(x =>
                            x.article.id === l.article.id ? { ...x, prix_unitaire: val } : x));
                        }}
                        onFocus={(e) => setTimeout(() => e.target.select(), 0)}
                        size="small"
                        disabled={!peutModifierPrix}
                        inputProps={{ min: 0, step: 0.01, inputMode: 'decimal', style: { textAlign: 'right', width: 80, fontSize: 18 } }}
                      />
                    </TableCell>
                    {/* Prix unitaire TTC (avant remise) — calculé depuis le HT saisi, lecture seule */}
                    <TableCell align="right">
                      {formatNumberWithSpaces(l.prix_unitaire * (1 + (l.tva ?? 0) / 100))}
                    </TableCell>
                    {peutRemise && <TableCell align="right">
                      <TextField
                        type="number"
                        value={l.remise}
                        onChange={(e) => {
                          const saisie = Number(e.target.value);
                          const max    = l.article.remise ?? 100;
                          if (saisie > max) {
                            showSnackbar(`Remise max pour ${l.article.code} : ${max}%`, 'warning');
                          }
                          const clamped = Math.min(Math.max(0, saisie), max);
                          updateActiveCart(prev => prev.map(x =>
                            x.article.id === l.article.id ? { ...x, remise: clamped } : x));
                        }}
                        onFocus={(e) => setTimeout(() => e.target.select(), 0)}
                        size="small"
                        inputProps={{ min: 0, max: l.article.remise ?? 100, step: 1, inputMode: 'numeric', style: { textAlign: 'right', width: 60, fontSize: 18 } }}
                      />
                    </TableCell>}
                    <TableCell align="right">{formatNumberWithSpaces(computeLineTotal(l))}</TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => removeLine(l.article.id!)}>
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Paper>

        {/* Colonne droite : encaissement */}
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, overflow: 'auto', minHeight: 0 }}>
          <Typography variant="h6">Encaissement</Typography>

          <Box sx={{ p: 1.5, bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 1 }}>
            <Typography variant="caption">Total à payer</Typography>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>{formatNumberWithSpaces(totalTTC)}</Typography>
          </Box>

          <TextField
            label="Date"
            type="date"
            size="small"
            required
            value={dateVente}
            onChange={(e) => setDateVente(e.target.value)}
            InputLabelProps={{ shrink: true }}
            // Caisse restée ouverte après minuit, ou date changée exprès : bien visible.
            helperText={dateVente && dateVente !== dateDuJour() ? 'Différente de la date du jour' : undefined}
            FormHelperTextProps={{ sx: { color: '#b54708', fontWeight: 600 } }}
          />

          <SelectDepot
            selectedDepot={depot}
            onDepotChange={setDepot}
            departement_id={selectedDepartement?.id ?? null}
          />

          <SelectCaisse selectedCaisse={caisse} onCaisseChange={setCaisse} />

          <SelectModeReglement
            selectedModeReglement={modeReglement}
            onModeReglementChange={setModeReglement}
          />

          <ClientAutoC
            selectedClient={client}
            onClientChange={(c) => updateActive({ client: c })}
            required={!!factClientType?.tiers_obligatoire}
          />

          <TextField
            label="Montant encaissé"
            type="number"
            value={montantEncaisse}
            onChange={(e) => updateActive({ montantEncaisse: Number(e.target.value) })}
            onFocus={(e) => setTimeout(() => e.target.select(), 0)}
            size="small"
            inputProps={{ min: 0, step: 0.01, inputMode: 'decimal', style: { fontSize: 18 } }}
          />

          {montantEncaisse > totalTTC && (
            <Alert severity="info" sx={{ py: 0 }}>
              Rendu : <strong>{formatNumberWithSpaces(montantEncaisse - totalTTC)}</strong>
            </Alert>
          )}

          <Box sx={{ flex: 1 }} />

          <MyButton
            actionType={ButtonType.DEFAULT}
            variant="contained"
            size="large"
            onClick={handleValider}
            loading={loading}
            disabled={cart.length === 0}
          >
            Valider & Encaisser
          </MyButton>
        </Paper>
      </Box>

      {/* Ticket imprimé à la validation — caché à l'écran, visible uniquement en impression */}
      <PosTicket
        sale={lastSale}
        caisse={caisse?.nom}
        mode={modeReglement?.nom}
      />
    </Box>
  );
};

export default PointOfSale;
